import {
  Button,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Card,
  OutlinedInput,
  Grid,
  IconButton,
} from "@mui/material";

import {
  onChangeClinicNotes,
  changeExpansion,
} from "app/store/transmission/slice/transmissionSlice";

import React, { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";

import {
  Mic as MicIcon,
  ExpandMore as ExpandMoreIcon,
  SpeakerNotes as SpeakerNotesIcon,
} from "@mui/icons-material";
import NotesIcon from "@mui/icons-material/Notes";

import { truncate } from "utils/string";
import {
  USER_ROLES,
  CLINIC_ACTIVE,
  CLINIC_FOR_REVIEW,
  HF_BILLING_CODES,
} from "static/constants";
import { getCurrentUserRole } from "utils/currentUser.util";
import ChfScreening from "../chf_screening/ChfScreening";
import QuickTextTemplates from "../quick_text_templates/QuickTextTemplates";
import ProviderNotes from "../provider_notes/ProviderNotes";
import SubmitButton from "components/Button/SubmitButton";
import { formatLocalDateTime } from "utils/date";
import useClinicNotesWithProviderNotes from "./useClinicNotesWithProviderNotes";
import {
  NOTE_SAVE_HANDLERS,
  useRegisterNoteSaveHandler,
} from "../NoteSaveHandlersContext";
import useNoteButtonActionInProgress from "../footer/hooks/useNoteButtonActionInProgress";

const ClinicNotesWithProviderNotes = ({ data }) => {
  const accordionRef = React.useRef(null);
  const {
    theme,
    dispatch,
    reportDetail,
    clinicNotes,
    newClinicNote,
    clinicNotesExpanded,
    handleInputChange,
    flushClinicNote,
    clinicNotesEsig,
    cancelClinicNotes,
    onSaveClinicNotesWithQuickText,
    onSaveClinicNotes,
    clinicNotesTouched,
    isSpeechRecognizing,
    toggleSpeechRecognition,
    clinicNoteSubmitting,
  } = useClinicNotesWithProviderNotes(data);

  const isActionInProgress = useNoteButtonActionInProgress();
  const { accountsMapper } = useSelector((state) => state.account);
  const { reportCurrentTab } = useSelector((state) => state.transmission);
  const { isArchiveReport } = useSelector((state) => state.archiveReport);
  const userRole = getCurrentUserRole();

  const showCHFScreening =
    accountsMapper?.[data?.account]?.settings?.show_chf_screening &&
    HF_BILLING_CODES.includes(data?.billing_code);

  const showQuickTextTemplates =
    accountsMapper?.[data?.account]?.settings?.show_quick_text_templates ||
    false;

  const isClinicUser = useMemo(
    () => [USER_ROLES.CLINIC, USER_ROLES.CLINIC_ADMIN].includes(userRole),
    [userRole]
  );
  const isPhysicianUser = userRole === USER_ROLES.PHYSICIAN;

  const isActiveTab = reportCurrentTab === CLINIC_ACTIVE;
  const isReviewTab = reportCurrentTab === CLINIC_FOR_REVIEW;

  const shouldShowClinicNotes = (isClinicUser && isActiveTab) || newClinicNote;

  const shouldShowProviderNotes =
    (isPhysicianUser && isReviewTab) || reportDetail?.provider_note?.note;

  const canEditNotes = useMemo(
    () =>
      !isArchiveReport &&
      !data?.locked &&
      !data?.legacy &&
      !clinicNotesEsig &&
      (isClinicUser ? isActiveTab : true),
    [
      isArchiveReport,
      data?.locked,
      data?.legacy,
      clinicNotesEsig,
      isClinicUser,
      isActiveTab,
    ]
  );

  const showActionButtons = useMemo(
    () =>
      !data.legacy &&
      !data?.locked &&
      !isArchiveReport &&
      isClinicUser &&
      isActiveTab,
    [data.legacy, data?.locked, isArchiveReport, isClinicUser, isActiveTab]
  );

  useRegisterNoteSaveHandler(
    NOTE_SAVE_HANDLERS.CLINIC_NOTE_SAVE,
    (isProcessAction = false, isSignAction = false, processState) =>
      isProcessAction
        ? onSaveClinicNotes(isProcessAction, isSignAction, processState)
        : onSaveClinicNotesWithQuickText()
  );

  useRegisterNoteSaveHandler(NOTE_SAVE_HANDLERS.CLINIC_NOTE_UNSAVED, () => {
    // Flush text still in the debounce
    flushClinicNote();
    return (
      canEditNotes &&
      (clinicNotesTouched || (newClinicNote || "") !== (clinicNotes || ""))
    );
  });

  const handleTemplateClick = useCallback(
    (description) => {
      dispatch(
        onChangeClinicNotes({
          note: newClinicNote
            ? `${newClinicNote}\n${description}`
            : description,
          notesTouched: true,
        })
      );
    },
    [dispatch, newClinicNote]
  );

  const isViewMode = !(isClinicUser && canEditNotes);

  return (
    <>
      {(shouldShowClinicNotes || shouldShowProviderNotes) && (
        <Accordion
          className="view-pdf__panel"
          expanded={clinicNotesExpanded}
          onChange={(e, expanded) => {
            dispatch(changeExpansion({ type: "clinicNotes", value: expanded }));
          }}
          style={{
            boxShadow: "none",
            borderColor:
              theme.palette.type === "dark"
                ? "rgba(255, 255, 255, 0.12)"
                : "#E0E0E0",
            marginBottom: "16px",
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon data-action="expand-more-clinic-notes-icon" />
            }
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <span
              className="view-pdf__notes-header--icon"
              style={{ margin: 0 }}
            >
              <SpeakerNotesIcon />
            </span>
            <div
              className="view-pdf__notes-header--title ml-2"
              style={{ margin: 0, fontSize: "14px" }}
            >
              {data?.account ? truncate(data?.account, 50) : ""}
            </div>
          </AccordionSummary>
          <AccordionDetails
            ref={accordionRef}
            className="view-pdf__details"
            style={{
              boxShadow: "none",
              border: "1px solid #E0E0E0",
              background: "#FAFAFA",
            }}
          >
            {showCHFScreening && (
              <ChfScreening
                reportDetail={reportDetail}
                isActiveOrSingleTab={isActiveTab}
              />
            )}

            {isClinicUser && showQuickTextTemplates && !isViewMode && (
              <QuickTextTemplates
                account={data?.account}
                onTemplateClick={handleTemplateClick}
                disabled={isActionInProgress}
              />
            )}

            {shouldShowClinicNotes && (
              <Card
                style={{
                  margin: "12px 0",
                  boxShadow: "none",
                  border: "1px solid #E0E0E0",
                }}
              >
                <Grid container className="d-flex align-items-center p-2">
                  <Grid item xs={1}>
                    <NotesIcon className="mr-2" />
                  </Grid>
                  <Grid item xs={11}>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#444444",
                        fontWeight: 500,
                      }}
                    >
                      Clinic Notes
                    </span>
                  </Grid>
                </Grid>
                <Divider className="mb-2" />

                <div className={isViewMode ? "p-3 pt-0 pb-0" : "p-3"}>
                  {isViewMode ? (
                    <div className="view-pdf__textarea view-mode mb-0 p-0 border-none">
                      {newClinicNote}
                    </div>
                  ) : (
                    <div className="d-flex mt-2">
                      <OutlinedInput
                        minRows={3}
                        maxRows={10}
                        multiline
                        fullWidth
                        variant="outlined"
                        id="clinic-note"
                        data-text="clinic-note-field"
                        placeholder="Write notes here"
                        value={newClinicNote}
                        inputProps={{
                          spellCheck: true,
                        }}
                        onChange={(e) => handleInputChange(e.target.value)}
                        disabled={isActionInProgress}
                        style={{
                          color: theme.palette.text.primary,
                          backgroundColor: theme.palette.background.paper,
                        }}
                        endAdornment={
                          <IconButton
                            color={isSpeechRecognizing ? "primary" : "default"}
                            onClick={toggleSpeechRecognition}
                            disabled={isActionInProgress}
                            className={
                              isSpeechRecognizing ? "mic-pulse" : undefined
                            }
                            aria-label={
                              isSpeechRecognizing
                                ? "Listening..."
                                : "Start voice dictation"
                            }
                          >
                            <MicIcon />
                          </IconButton>
                        }
                      />
                    </div>
                  )}

                  <div className="process-buttons__wrapper mt-2">
                    <div className="d-flex justify-between">
                      <div
                        className="view-pdf__notes-signature"
                        style={{ marginBottom: "8px" }}
                      >
                        {reportDetail?.clinicSignature?.name && (
                          <>
                            <span>{reportDetail?.clinicSignature?.name}</span>{" "}
                            •&nbsp;
                          </>
                        )}
                        {reportDetail?.clinicSignature?.updated_at && (
                          <span>
                            {formatLocalDateTime(
                              reportDetail?.clinicSignature?.updated_at
                            )}
                          </span>
                        )}
                      </div>

                      {showActionButtons && (
                        <div
                          className="d-flex align-items-center flex-end"
                          style={{
                            gap: "10px",
                            marginTop: "10px",
                            marginBottom: "8px",
                          }}
                        >
                          <Button
                            onClick={cancelClinicNotes}
                            className="view-pdf__button"
                            variant="outlined"
                            color="secondary"
                            disabled={isActionInProgress}
                          >
                            Cancel
                          </Button>

                          <SubmitButton
                            handleClick={() => onSaveClinicNotesWithQuickText()}
                            className="view-pdf__button"
                            variant="contained"
                            color="secondary"
                            hidden={!isActiveTab}
                            isLoading={clinicNoteSubmitting}
                            style={{
                              opacity: !clinicNotesTouched ? ".3" : "1",
                            }}
                            disabled={!clinicNotesTouched || isActionInProgress}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {shouldShowProviderNotes && <ProviderNotes data={data} />}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default ClinicNotesWithProviderNotes;
